/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    UpcomingOffersApi,
    UpcomingOffersGetRequest,
} from 'client-code/upcoming-offers';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IUpcomingOffersServiceRepository } from '../interfaces';
import { Log } from '../logging';
import { TServicesParams, TUpcomingOffersList, TUpcomingOffersParams } from '../types';
import { UniqueIdentifier } from '../utils';

export class UpcomingOffersServiceRepositoryImpl implements IUpcomingOffersServiceRepository {
    private static TAG: string = 'UpcomingOffersServiceRepositoryImpl';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getUpcomingOffersFromService(
        upcomingOffersParams: TUpcomingOffersParams,
    ): Promise<TUpcomingOffersList> {
        try {
            const { objectId, offset, limit, minEndTime, screenType, requestToken, networkCallId } =
                upcomingOffersParams;
            const commonHeaders = new CommonHeaders(screenType).headers;

            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: UpcomingOffersGetRequest = {
                objectId,
                offset,
                limit,
                minEndTime,
                requestToken,
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                applicationName: commonHeaders.ApplicationName,
                deviceType: commonHeaders.DeviceType,
                productName: commonHeaders.ProductName,
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
                whiny: this.mParams.whiny || 'false',
            };

            const configuration = new Configuration(configurationParameters);
            const upcomingOffersApi = new UpcomingOffersApi(configuration);
            const upcomingOffersApiResponse =
                await upcomingOffersApi.upcomingOffersGet(requestParameters);

            const totalCount = parseInt(upcomingOffersApiResponse.headers['x-total-count'], 10);

            return { ...upcomingOffersApiResponse.data, totalCount, networkCallId };
        } catch (error: any) {
            Log.e(UpcomingOffersServiceRepositoryImpl.TAG, 'getUpcomingOffersFromService()', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchUpcomingOffers(
        upcomingOffersParams: TUpcomingOffersParams,
    ): Promise<TUpcomingOffersList> {
        try {
            return await this.getUpcomingOffersFromService(upcomingOffersParams);
        } catch (error) {
            Log.e(UpcomingOffersServiceRepositoryImpl.TAG, 'error', error);
            throw error;
        }
    }
}
